import InputField from "components/fields/InputField";
import { useAuthTokens } from "hooks/useAuthTokens";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";
import { useLoginMutation } from "reduxFiles/api/authApi";
import { LoginRequest } from "types/authTypes";
import { useForm } from "react-hook-form";
import { useAppDispatch } from "reduxFiles/hooks";
import { addNotification } from "reduxFiles/slices/notificationSlice";
import { motion } from "framer-motion";
import Button from "components/button/Button";

export default function OrganizationLogin() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const saveTokensAndUser = useAuthTokens().saveTokensAndUser;
  const [login, { isLoading }] = useLoginMutation();

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginRequest>({
    mode: "onSubmit",
    reValidateMode: "onChange",
  });

  const onSubmit = async (data: LoginRequest) => {
    try {
      const result = await login(data).unwrap();

      saveTokensAndUser(result);
      navigate("/organization/dashboard");

      dispatch(
        addNotification({
          type: "success",
          message: t("auth.organizationLogin.success"),
        })
      );
    } catch (error: any) {
      console.error(error);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, ease: "easeOut" }}
      className="flex h-full w-full items-center justify-center"
    >
      <div className="w-full max-w-md px-2 sm:px-4">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.1, duration: 0.5 }}
          className="mb-4 sm:mb-6"
        >
          <div className="mb-1 flex items-center gap-2">
            <div className="h-1 w-8 rounded-full bg-gradient-to-r from-brand-500 to-indigo-500" />
            <span className="text-xs font-medium uppercase tracking-wider text-brand-500 dark:text-brand-100">
              {t("auth.organizationLogin.badge") || "Organization"}
            </span>
          </div>
          <h4 className="text-2xl font-bold text-navy-700 dark:text-white sm:text-3xl">
            {t("auth.organizationLogin.title")}
          </h4>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            {t("auth.organizationLogin.description")}
          </p>
        </motion.div>

        {/* Form Container */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2, duration: 0.5 }}
          className="relative"
        >
          <form
            onSubmit={handleSubmit(onSubmit)}
            noValidate
            className="space-y-3"
          >
            {/* Email */}
            <InputField
              variant="auth"
              extra="mb-0"
              label={t("auth.fields.email")}
              placeholder={t("auth.placeholders.email")}
              id="email"
              type="email"
              autoFocus
              {...register("email", {
                required: t("validation.required"),
                pattern: {
                  value: /\S+@\S+\.\S+/,
                  message: t("validation.email"),
                },
              })}
              state={errors.email ? "error" : ""}
              errorMessage={errors.email?.message}
            />

            {/* Password */}
            <InputField
              variant="auth"
              extra="mb-0"
              label={t("auth.fields.password")}
              placeholder={t("auth.placeholders.password")}
              id="password"
              type="password"
              {...register("password", {
                required: t("validation.required"),
                minLength: {
                  value: 8,
                  message: t("validation.minLength", { min: 8 }),
                },
              })}
              state={errors.password ? "error" : ""}
              errorMessage={errors.password?.message}
            />

            {/* Forgot Password Link */}
            <div className="flex items-center justify-end px-1">
              <Link
                to="/auth/reset-password"
                className="text-xs font-medium text-brand-500 transition-all hover:text-brand-600 hover:underline dark:text-brand-100"
              >
                {t("auth.organizationLogin.forgotPassword")}
              </Link>
            </div>

            {/* Submit Button */}
            <Button
              type="submit"
              variant="primary"
              size="lg"
              fullWidth
              loading={isLoading}
              className="mt-1"
            >
              {t("auth.organizationLogin.submit")}
            </Button>
          </form>
        </motion.div>

        {/* Footer Links */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3, duration: 0.5 }}
          className="mt-4 space-y-3 border-t border-gray-200 pt-4 dark:border-navy-700"
        >
          {/* Create Organization Account */}
          {/* <div className="flex flex-wrap items-center justify-center gap-1">
            <span className="text-xs text-gray-600 dark:text-gray-400">
              {t("auth.organizationLogin.notRegistered")}
            </span>
            <Link
              to="/auth/sign-up"
              className="text-xs font-medium text-brand-500 transition-all hover:text-brand-600 hover:underline dark:text-brand-100"
            >
              {t("auth.organizationLogin.createAccount")}
            </Link>
          </div> */}

          {/* Responder Login */}
          <div className="flex flex-wrap items-center justify-center gap-1">
            <span className="text-xs text-gray-600 dark:text-gray-400">
              {t("auth.organizationLogin.loginResponder")}
            </span>
            <Link
              to="/assessment"
              className="text-xs font-medium text-brand-500 transition-all hover:text-brand-600 hover:underline dark:text-brand-100"
            >
              {t("auth.organizationLogin.loginResponderButton")}
            </Link>
          </div>

          {/* Back to Normal Login */}
          <div className="flex flex-wrap items-center justify-center gap-1 border-t border-gray-100 pt-2 dark:border-navy-800">
            <span className="text-xs text-gray-400 dark:text-gray-500">
              {t("auth.organizationLogin.backToNormal")}
            </span>
            <Link
              to="/auth/sign-in"
              className="text-xs font-medium text-brand-500 transition-all hover:text-brand-600 hover:underline dark:text-brand-100"
            >
              {t("auth.organizationLogin.normalLogin")}
            </Link>
          </div>
        </motion.div>
      </div>
    </motion.div>
  );
}
